import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Calendar, MapPin, Video, Building2 } from "lucide-react";

export default function MeetingModal({ open, lead, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: "", time: "",
    meeting_type: "offline",  // "online" | "offline"
    meeting_link: "",
    branch_id: "",
    location_text: "",
  });
  const [branches, setBranches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/branches").then(r => setBranches(r.data || [])).catch(() => setBranches([]));
  }, [open]);

  useEffect(() => {
    if (!open || !lead) return;
    setErr("");
    const m = lead.meeting || {};
    setForm({
      date: m.date || "",
      time: m.time || "",
      meeting_type: m.meeting_type || "offline",
      meeting_link: m.meeting_link || "",
      branch_id: m.branch_id || "",
      location_text: m.location_text || "",
    });
  }, [open, lead]);

  const selectedBranch = useMemo(
    () => branches.find(b => b.id === form.branch_id),
    [branches, form.branch_id]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.time) { setErr("Date and time are mandatory"); return; }
    if (form.meeting_type === "offline" && !form.location_text.trim() && !form.branch_id) {
      setErr("Pick a branch or add a venue address");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/sales/leads/${lead.id}/schedule-meeting`, {
        date: form.date,
        time: form.time,
        meeting_type: form.meeting_type,
        meeting_link: form.meeting_type === "online" ? (form.meeting_link.trim() || null) : null,
        branch_id: form.meeting_type === "offline" ? (form.branch_id || null) : null,
        location_text: form.meeting_type === "offline" ? (form.location_text.trim() || null) : null,
      });
      toast.success("Meeting scheduled");
      onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !lead) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="meeting-modal">
      <div className="w-full max-w-md iz-card p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
            <Calendar className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5}/>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Checkpoint</p>
            <h2 className="font-display text-xl text-white">Schedule meeting</h2>
            <p className="text-[11px] text-gray-500 font-mono">{lead.client_name}</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Date *</label>
              <input type="date" value={form.date} onChange={(e) => setForm(f => ({...f, date: e.target.value}))} className="iz-input" required data-testid="meeting-date"/>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Time *</label>
              <input type="time" value={form.time} onChange={(e) => setForm(f => ({...f, time: e.target.value}))} className="iz-input" required data-testid="meeting-time"/>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Meeting Type *</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setForm(f => ({...f, meeting_type: "online"}))}
                      className={`iz-btn-secondary flex items-center justify-center gap-2 py-2 ${form.meeting_type === "online" ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/5" : ""}`}
                      data-testid="meeting-type-online">
                <Video className="w-4 h-4"/> Online
              </button>
              <button type="button" onClick={() => setForm(f => ({...f, meeting_type: "offline"}))}
                      className={`iz-btn-secondary flex items-center justify-center gap-2 py-2 ${form.meeting_type === "offline" ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/5" : ""}`}
                      data-testid="meeting-type-offline">
                <Building2 className="w-4 h-4"/> Offline
              </button>
            </div>
          </div>

          {form.meeting_type === "online" && (
            <div data-testid="meeting-online-fields">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Meeting Link (optional)</label>
              <input type="url" value={form.meeting_link}
                     onChange={(e) => setForm(f => ({...f, meeting_link: e.target.value}))}
                     className="iz-input" placeholder="https://meet.google.com/xyz-abcd-efg"
                     data-testid="meeting-link"/>
            </div>
          )}

          {form.meeting_type === "offline" && (
            <div className="space-y-4" data-testid="meeting-offline-fields">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Branch</label>
                <select value={form.branch_id}
                        onChange={(e) => setForm(f => ({...f, branch_id: e.target.value}))}
                        className="iz-input" data-testid="meeting-branch">
                  <option value="">— Select branch —</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}{b.city ? ` · ${b.city}` : ""}</option>
                  ))}
                </select>
                {selectedBranch?.address && (
                  <p className="text-[10px] text-gray-500 mt-1">{selectedBranch.address}</p>
                )}
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Venue Notes / Address</label>
                <input value={form.location_text}
                       onChange={(e) => setForm(f => ({...f, location_text: e.target.value}))}
                       className="iz-input" placeholder="e.g. Conference Room B, 3rd floor"
                       data-testid="meeting-location"/>
              </div>
            </div>
          )}

          <div className="text-xs bg-[#0f0f0f] border border-[#d4af37]/30 rounded-md p-3 flex items-start gap-2">
            <MapPin className="w-3.5 h-3.5 text-[#d4af37] flex-shrink-0 mt-0.5"/>
            <span className="text-gray-400">SMS + push reminder auto-triggers to executive & client 12h before meeting.</span>
          </div>

          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary" data-testid="meeting-submit">
              {saving ? "Saving…" : "Confirm Meeting"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
